/**
 * 解析タイプ → チャート本体の単一ルーティング（図ペイン／設定ペインで共有）。
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeisukeDashboardChartSource } from './useKeisukeDashboardChartSource';
import KeisukeDashboardCategoryChart, {
  type KeisukeDashboardCategoryChartLayout,
} from './KeisukeDashboardCategoryChart';
import KeisukeDashboardDoseResponseChart from './KeisukeDashboardDoseResponseChart';
import KeisukeDashboardSurvivalChart from './KeisukeDashboardSurvivalChart';
import KeisukeDashboardRegressionChart from './KeisukeDashboardRegressionChart';
import KeisukeDashboardLongitudinalChart from './KeisukeDashboardLongitudinalChart';
import styles from './KeisukeDashboardChart.module.scss';

export type KeisukeDashboardChartRouterProps = {
  layout: KeisukeDashboardCategoryChartLayout;
  isCalculating?: boolean;
};

export default function KeisukeDashboardChartRouter({
  layout,
  isCalculating = false,
}: KeisukeDashboardChartRouterProps): ReactNode {
  const { t } = useTranslation();
  const {
    analysisOptions,
    resultCache,
    chartType,
    significancePairs,
    barChartGridData,
    groupCol,
    valueCol,
    valueAxisLabel,
  } = useKeisukeDashboardChartSource();

  const mapping = analysisOptions.mapping;
  const isSettings = layout === 'split-settings';

  if (chartType === 'qpcr' && resultCache?.qpcrChart?.bars?.length) {
    const qpcrGrid = resultCache.qpcrChart.bars.map((b) => ({ group: b.label, value: b.value }));
    return (
      <KeisukeDashboardCategoryChart
        layout={layout}
        gridData={qpcrGrid}
        groupCol="group"
        valueCol="value"
        significancePairs={significancePairs}
        isCalculating={isCalculating}
        exportFileBaseName="keisuke_qpcr"
      />
    );
  }

  if (
    chartType === 't-test' ||
    chartType === 'anova' ||
    chartType === 'bar-chart' ||
    chartType === 'basic-stats'
  ) {
    /** 図ペインでデータ未投入のときはプレースホルダ（2列表の列予約用） */
    if (isSettings || barChartGridData.length > 0) {
      return (
        <KeisukeDashboardCategoryChart
          layout={layout}
          gridData={barChartGridData}
          groupCol={groupCol}
          valueCol={valueCol}
          valueAxisLabel={valueAxisLabel}
          significancePairs={chartType === 'bar-chart' ? [] : significancePairs}
          isCalculating={isCalculating}
          exportFileBaseName={chartType === 'basic-stats' ? 'keisuke_basic_stats' : 'keisuke_bar'}
        />
      );
    }
  }

  if (chartType === 'regression' && resultCache?.regressionChart) {
    const { observed, line, unknown } = resultCache.regressionChart;
    return (
      <KeisukeDashboardRegressionChart
        layout={layout}
        observed={observed}
        line={line}
        unknown={unknown}
        isCalculating={isCalculating}
        xLabel={mapping?.doseColumn ?? ''}
        yLabel={mapping?.valueColumn ?? ''}
      />
    );
  }

  if (chartType === 'longitudinal' && resultCache?.longitudinalChart) {
    return (
      <KeisukeDashboardLongitudinalChart
        layout={layout}
        chart={resultCache.longitudinalChart}
        isCalculating={isCalculating}
        timeLabel={mapping?.timeColumn ?? ''}
        valueLabel={mapping?.valueColumn ?? ''}
      />
    );
  }

  if (chartType === 'dose-response' && (isSettings || resultCache?.doseResponseChart)) {
    const dr = resultCache?.doseResponseChart;
    const drType = String(analysisOptions.drType ?? 'ld50');
    const isTcid50 = drType === 'tcid50';
    const doseCol = mapping?.doseColumn ?? '';
    const effectCol = mapping?.effectColumn ?? '';
    return (
      <KeisukeDashboardDoseResponseChart
        layout={layout}
        observed={dr?.observed ?? []}
        curve={dr?.curve}
        isCalculating={isCalculating}
        drType={isSettings ? undefined : drType}
        tableDoseColumnLabel={
          isSettings
            ? doseCol
            : doseCol || (isTcid50 ? t('keisuke.doseResponse.dilution') : t('keisuke.doseResponse.dose'))
        }
        tableEffectColumnLabel={
          isSettings
            ? effectCol
            : isTcid50
              ? t('keisuke.doseResponse.infectionRate')
              : effectCol || t('keisuke.doseResponse.responseRate')
        }
      />
    );
  }

  if (chartType === 'survival' && (isSettings || (resultCache?.survivalChart?.length ?? 0) > 0)) {
    return (
      <KeisukeDashboardSurvivalChart
        layout={layout}
        survivalChart={resultCache?.survivalChart ?? []}
        isCalculating={isCalculating}
        tableTimeColumnLabel={mapping?.timeColumn ?? ''}
      />
    );
  }

  if (isSettings) return null;

  return (
    <div
      className={`${styles.chartRoot} ${styles.chartRootWrapper} ${styles.chartPlaceholder} ${styles.chartPlaceholderCard}`}
      aria-hidden={isCalculating}
    >
      <span className={styles.chartPlaceholderText}>
        {chartType === 'dose-response' || chartType === 'survival'
          ? t('keisuke.workspace.chartRunAnalysis')
          : t('keisuke.workspace.chartNoData')}
      </span>
    </div>
  );
}
